import React, { useRef, useState, useCallback, useEffect } from "react";
import { useDAW } from "./DAWProvider";
import { MIDI_MIN, MIDI_MAX, MIDI_COUNT, midiToNoteName, isBlackKey } from "./midiConstants";
import { PIXELS_PER_BEAT } from "./TimelineRuler";

const ROW_HEIGHT = 14;
const SUBDIVISION = 4; // 16th notes
const SNAP_WIDTH = PIXELS_PER_BEAT / SUBDIVISION;
const NOTE_EDGE_ZONE = 6; // px for resize handle
const DEFAULT_VELOCITY = 100;
const DRAG_THRESHOLD = 4; // px before a click becomes a drag

export default function PianoRoll({ track }) {
  const { updateInstrumentNotes, bpm, arrangementBars, noteIdCounter } = useDAW();
  const canvasRef = useRef(null);
  const gridRef = useRef(null);
  const sidebarRef = useRef(null);

  const [selectedNoteIds, setSelectedNoteIds] = useState(new Set());
  const [marquee, setMarquee] = useState(null); // { x, y, w, h } in canvas coords
  const dragRef = useRef(null);

  const totalBeats = arrangementBars * 4;
  const gridWidth = totalBeats * PIXELS_PER_BEAT;
  const gridHeight = MIDI_COUNT * ROW_HEIGHT;

  // Draw the note grid on canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    canvas.width = gridWidth * dpr;
    canvas.height = gridHeight * dpr;
    canvas.style.width = `${gridWidth}px`;
    canvas.style.height = `${gridHeight}px`;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, gridWidth, gridHeight);

    // Draw row backgrounds
    for (let i = 0; i < MIDI_COUNT; i++) {
      const midi = MIDI_MAX - i;
      const y = i * ROW_HEIGHT;
      if (isBlackKey(midi)) {
        ctx.fillStyle = "rgba(0, 0, 0, 0.2)";
        ctx.fillRect(0, y, gridWidth, ROW_HEIGHT);
      }
      if (midi % 12 === 0) {
        ctx.strokeStyle = "rgba(255, 255, 255, 0.1)";
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(gridWidth, y);
        ctx.stroke();
      }
    }

    // Draw vertical grid lines
    for (let beat = 0; beat <= totalBeats; beat++) {
      const x = beat * PIXELS_PER_BEAT;
      const isBar = beat % 4 === 0;
      ctx.strokeStyle = isBar
        ? "rgba(255, 255, 255, 0.12)"
        : "rgba(255, 255, 255, 0.04)";
      ctx.lineWidth = isBar ? 1 : 0.5;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, gridHeight);
      ctx.stroke();

      if (beat < totalBeats) {
        for (let sub = 1; sub < SUBDIVISION; sub++) {
          const sx = x + sub * SNAP_WIDTH;
          ctx.strokeStyle = "rgba(255, 255, 255, 0.02)";
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(sx, 0);
          ctx.lineTo(sx, gridHeight);
          ctx.stroke();
        }
      }
    }

    // Draw notes
    track.notes.forEach((note) => {
      const row = MIDI_MAX - note.midi;
      const y = row * ROW_HEIGHT + 1;
      const x = note.start * PIXELS_PER_BEAT;
      const w = Math.max(4, note.duration * PIXELS_PER_BEAT);
      const h = ROW_HEIGHT - 2;

      const isSelected = selectedNoteIds.has(note.id);

      ctx.fillStyle = isSelected
        ? "rgba(238, 119, 47, 0.9)"
        : "rgba(186, 104, 200, 0.7)";
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 2);
      ctx.fill();

      // Right edge indicator
      ctx.fillStyle = isSelected
        ? "rgba(255, 255, 255, 0.5)"
        : "rgba(255, 255, 255, 0.2)";
      ctx.fillRect(x + w - 3, y, 3, h);
    });
  }, [track.notes, gridWidth, gridHeight, totalBeats, selectedNoteIds]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Hit test: find note at position
  const hitTest = useCallback((x, y) => {
    for (let i = track.notes.length - 1; i >= 0; i--) {
      const note = track.notes[i];
      const noteX = note.start * PIXELS_PER_BEAT;
      const noteW = Math.max(4, note.duration * PIXELS_PER_BEAT);
      const row = MIDI_MAX - note.midi;
      const noteY = row * ROW_HEIGHT;

      if (x >= noteX && x <= noteX + noteW && y >= noteY && y <= noteY + ROW_HEIGHT) {
        const isRightEdge = x > noteX + noteW - NOTE_EDGE_ZONE;
        return { note, mode: isRightEdge ? "resize" : "move" };
      }
    }
    return null;
  }, [track.notes]);

  const snapBeat = (beat) => {
    const step = 1 / SUBDIVISION;
    return Math.max(0, Math.round(beat / step) * step);
  };

  const getMidiFromY = (y) => {
    const row = Math.floor(y / ROW_HEIGHT);
    return Math.max(MIDI_MIN, Math.min(MIDI_MAX, MIDI_MAX - row));
  };

  const getBeatFromX = (x) => x / PIXELS_PER_BEAT;

  // Duplicate selected notes, offset slightly, return the new notes and a map old->new id
  const duplicateNotes = useCallback((noteIds) => {
    const toDuplicate = track.notes.filter((n) => noteIds.has(n.id));
    const idMap = {};
    const copies = toDuplicate.map((n) => {
      const newId = noteIdCounter();
      idMap[n.id] = newId;
      return { ...n, id: newId };
    });
    return { copies, idMap };
  }, [track.notes, noteIdCounter]);

  // Get the "anchor" note for multi-note drag (used to compute delta)
  const getSelectedNotes = useCallback(() => {
    return track.notes.filter((n) => selectedNoteIds.has(n.id));
  }, [track.notes, selectedNoteIds]);

  const handlePointerDown = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const hit = hitTest(x, y);

    if (hit) {
      // --- Clicked on a note ---
      if (e.button === 2) {
        // Right-click: delete
        e.preventDefault();
        if (selectedNoteIds.has(hit.note.id) && selectedNoteIds.size > 1) {
          // Delete all selected
          const newNotes = track.notes.filter((n) => !selectedNoteIds.has(n.id));
          updateInstrumentNotes(track.id, newNotes);
        } else {
          const newNotes = track.notes.filter((n) => n.id !== hit.note.id);
          updateInstrumentNotes(track.id, newNotes);
        }
        setSelectedNoteIds(new Set());
        return;
      }

      // Option+drag = duplicate
      if (e.altKey) {
        // If the clicked note isn't selected, select only it
        let ids = selectedNoteIds;
        if (!selectedNoteIds.has(hit.note.id)) {
          ids = new Set([hit.note.id]);
        }

        const { copies, idMap } = duplicateNotes(ids);
        // Add copies to track
        const newNotes = [...track.notes, ...copies];
        updateInstrumentNotes(track.id, newNotes);

        // Select the copies, drag them
        const newIds = new Set(Object.values(idMap));
        setSelectedNoteIds(newIds);

        // Store originals for the copies so we can compute delta
        const originals = {};
        copies.forEach((c) => { originals[c.id] = { start: c.start, midi: c.midi }; });

        dragRef.current = {
          mode: "move-multi",
          startX: e.clientX,
          startY: e.clientY,
          originals, // { [newId]: { start, midi } }
        };
        canvasRef.current.setPointerCapture(e.pointerId);
        return;
      }

      // Shift+click = toggle selection
      if (e.shiftKey) {
        setSelectedNoteIds((prev) => {
          const next = new Set(prev);
          if (next.has(hit.note.id)) next.delete(hit.note.id);
          else next.add(hit.note.id);
          return next;
        });
        return;
      }

      // Normal click on note
      let currentSelection = selectedNoteIds;
      if (!selectedNoteIds.has(hit.note.id)) {
        currentSelection = new Set([hit.note.id]);
        setSelectedNoteIds(currentSelection);
      }

      if (hit.mode === "resize") {
        // Resize single note
        dragRef.current = {
          mode: "resize",
          noteId: hit.note.id,
          startX: e.clientX,
          origDuration: hit.note.duration,
        };
      } else {
        // Move selected notes
        const originals = {};
        track.notes.forEach((n) => {
          if (currentSelection.has(n.id)) {
            originals[n.id] = { start: n.start, midi: n.midi };
          }
        });

        dragRef.current = {
          mode: "move-multi",
          startX: e.clientX,
          startY: e.clientY,
          originals,
        };
      }
      canvasRef.current.setPointerCapture(e.pointerId);
    } else {
      // --- Clicked on empty space ---
      if (e.button === 2) return;

      // Could be: note creation (click) or marquee selection (drag)
      dragRef.current = {
        mode: "pending",
        startX: e.clientX,
        startY: e.clientY,
        canvasX: x,
        canvasY: y,
      };
      canvasRef.current.setPointerCapture(e.pointerId);
    }
  }, [hitTest, track, updateInstrumentNotes, selectedNoteIds, duplicateNotes]);

  const handlePointerMove = useCallback((e) => {
    if (!dragRef.current) {
      // Update cursor based on hover
      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const hit = hitTest(x, y);
      canvasRef.current.style.cursor = hit
        ? hit.mode === "resize" ? "ew-resize" : "grab"
        : "crosshair";
      return;
    }

    const drag = dragRef.current;
    const deltaX = e.clientX - drag.startX;
    const deltaY = e.clientY - drag.startY;

    if (drag.mode === "pending") {
      // Check if we've moved enough to become a drag
      if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
        // Convert to marquee selection
        drag.mode = "marquee";
        setSelectedNoteIds(new Set());
      }
      return;
    }

    if (drag.mode === "marquee") {
      const rect = canvasRef.current.getBoundingClientRect();
      const curX = e.clientX - rect.left;
      const curY = e.clientY - rect.top;

      const mx = Math.min(drag.canvasX, curX);
      const my = Math.min(drag.canvasY, curY);
      const mw = Math.abs(curX - drag.canvasX);
      const mh = Math.abs(curY - drag.canvasY);

      setMarquee({ x: mx, y: my, w: mw, h: mh });

      // Select notes within marquee
      const ids = new Set();
      track.notes.forEach((note) => {
        const noteX = note.start * PIXELS_PER_BEAT;
        const noteW = Math.max(4, note.duration * PIXELS_PER_BEAT);
        const row = MIDI_MAX - note.midi;
        const noteY = row * ROW_HEIGHT;
        const noteH = ROW_HEIGHT;

        // Intersect check
        if (noteX + noteW > mx && noteX < mx + mw && noteY + noteH > my && noteY < my + mh) {
          ids.add(note.id);
        }
      });
      setSelectedNoteIds(ids);
      return;
    }

    if (drag.mode === "move-multi") {
      const deltaBeats = deltaX / PIXELS_PER_BEAT;
      const deltaMidi = -Math.round(deltaY / ROW_HEIGHT);
      const snappedDeltaBeats = snapBeat(deltaBeats);

      const newNotes = track.notes.map((n) => {
        const orig = drag.originals[n.id];
        if (!orig) return n;
        return {
          ...n,
          start: Math.max(0, snapBeat(orig.start + snappedDeltaBeats)),
          midi: Math.max(MIDI_MIN, Math.min(MIDI_MAX, orig.midi + deltaMidi)),
        };
      });
      updateInstrumentNotes(track.id, newNotes);
      return;
    }

    if (drag.mode === "resize") {
      const deltaBeats = deltaX / PIXELS_PER_BEAT;
      const newDuration = Math.max(1 / SUBDIVISION, snapBeat(drag.origDuration + deltaBeats));
      const newNotes = track.notes.map((n) =>
        n.id === drag.noteId ? { ...n, duration: newDuration } : n
      );
      updateInstrumentNotes(track.id, newNotes);
      return;
    }

    if (drag.mode === "create") {
      const deltaBeats = deltaX / PIXELS_PER_BEAT;
      const newDuration = Math.max(1 / SUBDIVISION, snapBeat(drag.origDuration + deltaBeats));
      const newNotes = track.notes.map((n) =>
        n.id === drag.noteId ? { ...n, duration: newDuration } : n
      );
      updateInstrumentNotes(track.id, newNotes);
    }
  }, [hitTest, track, updateInstrumentNotes]);

  const handlePointerUp = useCallback((e) => {
    if (!dragRef.current) return;

    const drag = dragRef.current;

    if (drag.mode === "pending") {
      // Didn't drag — create a note at click position
      const midi = getMidiFromY(drag.canvasY);
      const beat = snapBeat(getBeatFromX(drag.canvasX));
      const newNote = {
        id: noteIdCounter(),
        midi,
        start: beat,
        duration: 1 / SUBDIVISION,
        velocity: DEFAULT_VELOCITY,
      };
      const newNotes = [...track.notes, newNote];
      updateInstrumentNotes(track.id, newNotes);
      setSelectedNoteIds(new Set([newNote.id]));

      // Allow extending the just-created note if the user keeps holding
      // (but since pointerUp already fired, this is just a click)
    }

    if (drag.mode === "marquee") {
      setMarquee(null);
    }

    canvasRef.current.releasePointerCapture(e.pointerId);
    dragRef.current = null;
  }, [track, updateInstrumentNotes, noteIdCounter]);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
  }, []);

  // Delete selected notes on Delete/Backspace, Cmd+A to select all
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;

      if ((e.key === "Delete" || e.key === "Backspace") && selectedNoteIds.size > 0) {
        e.preventDefault();
        const newNotes = track.notes.filter((n) => !selectedNoteIds.has(n.id));
        updateInstrumentNotes(track.id, newNotes);
        setSelectedNoteIds(new Set());
      }

      // Cmd/Ctrl+A = select all notes
      if ((e.metaKey || e.ctrlKey) && e.key === "a") {
        e.preventDefault();
        setSelectedNoteIds(new Set(track.notes.map((n) => n.id)));
      }

      // Cmd/Ctrl+D = duplicate selected
      if ((e.metaKey || e.ctrlKey) && e.key === "d" && selectedNoteIds.size > 0) {
        e.preventDefault();
        const toDuplicate = track.notes.filter((n) => selectedNoteIds.has(n.id));
        // Offset copies by 1 beat to the right
        const copies = toDuplicate.map((n) => ({
          ...n,
          id: noteIdCounter(),
          start: n.start + 1,
        }));
        const newNotes = [...track.notes, ...copies];
        updateInstrumentNotes(track.id, newNotes);
        setSelectedNoteIds(new Set(copies.map((c) => c.id)));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedNoteIds, track, updateInstrumentNotes, noteIdCounter]);

  // Sync sidebar scroll with grid scroll
  const handleGridScroll = useCallback(() => {
    if (gridRef.current && sidebarRef.current) {
      sidebarRef.current.scrollTop = gridRef.current.scrollTop;
    }
  }, []);

  return (
    <div className="daw-piano-roll">
      {/* Sidebar: note labels */}
      <div className="daw-piano-roll__sidebar" ref={sidebarRef}>
        {Array.from({ length: MIDI_COUNT }, (_, i) => {
          const midi = MIDI_MAX - i;
          const black = isBlackKey(midi);
          const isC = midi % 12 === 0;
          return (
            <div
              key={midi}
              className={
                "daw-piano-roll__key-label" +
                (black ? " daw-piano-roll__key-label--black" : "") +
                (isC ? " daw-piano-roll__key-label--c" : "")
              }
            >
              {isC || !black ? midiToNoteName(midi) : ""}
            </div>
          );
        })}
      </div>

      {/* Grid area */}
      <div
        className="daw-piano-roll__grid-area"
        ref={gridRef}
        onScroll={handleGridScroll}
      >
        <canvas
          ref={canvasRef}
          className="daw-piano-roll__canvas"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onContextMenu={handleContextMenu}
          style={{ width: gridWidth, height: gridHeight }}
        />
        {/* Marquee selection overlay */}
        {marquee && (
          <div
            className="daw-piano-roll__marquee"
            style={{
              left: marquee.x,
              top: marquee.y,
              width: marquee.w,
              height: marquee.h,
            }}
          />
        )}
      </div>
    </div>
  );
}
